import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./motion";
import { CATEGORIES } from "@/data/products";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full border-0 border-b border-vw-silver rounded-none bg-transparent px-0 py-3 text-vw-ink focus-visible:ring-0 focus-visible:border-vw-green placeholder:text-vw-ink2/50 transition-colors";

export const EnquiryFooter = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "", product: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.tValidation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(c.tSuccess);
      setForm({ name: "", company: "", email: "", country: "", product: "", message: "" });
    } catch (err) {
      toast.error(c.tError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-white">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-16">
        <Reveal className="md:col-span-5">
          <div className="vw-eyebrow text-vw-green mb-6">{c.eyebrow}</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter text-vw-ink">
            {c.letsPre}<span className="italic font-light text-vw-green">{c.letsEm}</span>
          </h2>
          <p className="mt-8 max-w-md text-lg text-vw-ink2 leading-relaxed">{c.para}</p>

          <div className="mt-12 space-y-6">
            <a href="mailto:aniiket@vardantaworld.com" data-testid="contact-email" className="group flex items-center justify-between border-t border-vw-silver pt-4 max-w-md">
              <span>
                <span className="vw-eyebrow text-vw-ink2 block mb-1">{c.emailLabel}</span>
                <span className="font-serif text-xl text-vw-ink">aniiket@vardantaworld.com</span>
              </span>
              <ArrowUpRight className="text-vw-ink2 group-hover:text-vw-green group-hover:-translate-y-0.5 transition-all" />
            </a>
            <a href="https://wa.me/919967272949" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-in" className="group flex items-center justify-between border-t border-vw-silver pt-4 max-w-md">
              <span>
                <span className="vw-eyebrow text-vw-ink2 block mb-1">{c.waIn}</span>
                <span className="font-serif text-xl text-vw-ink">+91 99672 72949</span>
              </span>
              <ArrowUpRight className="text-vw-ink2 group-hover:text-vw-green group-hover:-translate-y-0.5 transition-all" />
            </a>
            <a href="https://wa.me/447424811779" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-uk" className="group flex items-center justify-between border-t border-vw-silver pt-4 max-w-md">
              <span>
                <span className="vw-eyebrow text-vw-ink2 block mb-1">{c.waUk}</span>
                <span className="font-serif text-xl text-vw-ink">+44 74248 11779</span>
              </span>
              <ArrowUpRight className="text-vw-ink2 group-hover:text-vw-green group-hover:-translate-y-0.5 transition-all" />
            </a>
          </div>
        </Reveal>

        <Reveal className="md:col-span-7" delay={0.1}>
          <form data-testid="enquiry-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="vw-eyebrow text-vw-ink2">{c.name}</label>
              <Input data-testid="input-name" value={form.name} onChange={update("name")} placeholder={c.namePh} className={FIELD} />
            </div>
            <div>
              <label className="vw-eyebrow text-vw-ink2">{c.company}</label>
              <Input data-testid="input-company" value={form.company} onChange={update("company")} placeholder={c.companyPh} className={FIELD} />
            </div>
            <div>
              <label className="vw-eyebrow text-vw-ink2">{c.email}</label>
              <Input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder={c.emailPh} className={FIELD} />
            </div>
            <div>
              <label className="vw-eyebrow text-vw-ink2">{c.country}</label>
              <Input data-testid="input-country" value={form.country} onChange={update("country")} placeholder={c.countryPh} className={FIELD} />
            </div>
            <div className="sm:col-span-2">
              <label className="vw-eyebrow text-vw-ink2">{c.product}</label>
              <Select value={form.product} onValueChange={(v) => setForm((f) => ({ ...f, product: v }))}>
                <SelectTrigger data-testid="select-product" className="w-full border-0 border-b border-vw-silver rounded-none bg-transparent px-0 py-3 h-auto focus:ring-0 data-[placeholder]:text-vw-ink2/50">
                  <SelectValue placeholder={c.productPh} />
                </SelectTrigger>
                <SelectContent className="bg-white border-vw-silver rounded-none">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} data-testid={`option-${cat.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                      {t.cats[cat] || cat}
                    </SelectItem>
                  ))}
                  <SelectItem value="General enquiry" data-testid="option-general">{c.general}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="vw-eyebrow text-vw-ink2">{c.message}</label>
              <Textarea data-testid="input-message" value={form.message} onChange={update("message")} placeholder={c.messagePh} rows={4} className={`${FIELD} resize-none`} />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" data-testid="submit-enquiry-btn" disabled={loading} className="btn-sweep inline-flex items-center gap-2 bg-vw-green text-white px-10 py-4 text-sm font-medium hover:text-white transition-colors duration-500 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpRight size={16} />}
                {loading ? c.sending : c.submit}
              </button>
            </div>
          </form>
        </Reveal>
      </div>

      <footer className="bg-vw-green text-white">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-10 w-10 bg-white text-vw-green font-serif text-xl">VW</span>
              <span className="font-serif text-2xl">Vardanta World</span>
            </div>
            <p className="mt-5 text-white/60 max-w-xs leading-relaxed">{t.footer.tagline}</p>
          </div>
          <div>
            <div className="vw-eyebrow text-white/50 mb-4">{t.footer.reg}</div>
            <div className="font-mono text-sm text-white/80 space-y-2">
              <div>IEC · ABBFV6734E</div>
              <div>GST · 27ABBFV6734E1Z3</div>
            </div>
          </div>
          <div>
            <div className="vw-eyebrow text-white/50 mb-4">{t.footer.navigate}</div>
            <div className="flex flex-col gap-2 font-serif text-lg">
              <a href="#about" className="hover:text-vw-light transition-colors">{t.nav.about}</a>
              <a href="#products" className="hover:text-vw-light transition-colors">{t.nav.products}</a>
              <a href="#markets" className="hover:text-vw-light transition-colors">{t.nav.markets}</a>
              <a href="#contact" className="hover:text-vw-light transition-colors">{t.nav.contact}</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-white/40 text-xs font-mono">
            <span>© {new Date().getFullYear()} Vardanta World. {t.footer.rights}</span>
            <span>{t.footer.motto}</span>
          </div>
        </div>
      </footer>
    </section>
  );
};
